import { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-unresolved
import { ON24EliteTopNavBar } from '@on24/on24-elite-top-nav-bar/react';
import { useLocation } from 'react-router';
import { Helmet } from 'react-helmet';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { useEliteHeader } from './pageLayout.hooks';
import AppContext from '../App.context';
import { LoadingOverlay } from '../../common/loadingOverlay/LoadingOverlay.component';
import { PageLayoutShell } from './PageLayoutShell.component';
import { useQuery } from '../../common/hooks';

const StartOfContentLink = styled.a`
  background: ${({ theme }) => theme.palette.primary.main};
  color: ${({ theme }) => theme.palette.common.white};
  height: 1px;
  overflow: hidden;
  position: absolute;
  width: 1px;
  &:visited {
    color: ${({ theme }) => theme.palette.common.white};
  }
  &:focus {
    clip: auto;
    height: auto;
    padding: 16px;
    width: auto;
    z-index: 20;
  }
`;

export const AuthorizedPageWrapper = ({
  children,
}) => {
  const {
    t, globalSettingsLoaded, accountProperties,
  } = useContext(AppContext);

  const query = useQuery();
  const { isCommonEliteNavLoaded } = useEliteHeader();

  const { pathname } = useLocation();
  const topNavProductCode = useMemo(() => {
    if (pathname && query && accountProperties) {
      const arrPathName = pathname.split('/');
      if (arrPathName.includes('mediamanager')) return 'mediamanager';
      if (arrPathName.includes('accountdashboard') && query.get('tab') === 'branding') return 'branding';
      if (arrPathName.includes('accountdashboard') && query.get('tab') === 'autocorrect') return 'autocorrect';
      if (arrPathName.includes('home')) return 'home';
      if (arrPathName.includes('speakerlibrary')) return 'speakerlibrary';

      const webcastsPages = [
        'webcasts',
        'create',
        'update',
        'archive',
        'registration',
        'eventadmin',
        'captionlist',
        'managereg',
        'html5console',
        'series',
        'aicontent',
      ];
      if (webcastsPages.some((value) => arrPathName.includes(value))) {
        return accountProperties?.accountProperties?.isMeetupsSession ? 'meetups' : 'webcasts';
      }
    }
    return ' '; // Empty string fails. Null defaults to webcasts. Space selects nothing.
  }, [pathname, query, accountProperties]);

  const handleSkipToContentClick = (e) => {
    const elem = document.getElementById('startOfContent');
    function eventHandler() {
      elem.removeAttribute('tabindex');
      elem.removeEventListener('focusout', eventHandler);
    }
    e.preventDefault();
    elem.tabIndex = -1;
    elem.focus();
    elem.addEventListener('focusout', eventHandler);
  };
  const accountPropertyClientId = accountProperties.clientId;

  if (isCommonEliteNavLoaded && globalSettingsLoaded) {
    return (
      <>
        <Helmet>
          <link rel="stylesheet" href="https://maxcdn.bootstrapcdn.com/font-awesome/4.7.0/css/font-awesome.min.css" media="all" />
        </Helmet>
        <div className="top-nav" style={{ zIndex: 100 }}>

          <nav css={css`z-index: 99;`}>
            <StartOfContentLink
              href="#startOfContent"
              onClick={handleSkipToContentClick}
            >
              {t('SkipToContent')}
            </StartOfContentLink>
          </nav>

          <ON24EliteTopNavBar
            key={`${accountPropertyClientId}.${topNavProductCode}`}
            product-code={topNavProductCode}
            context="WCC"
            secondary-logo={accountProperties?.accountProperties?.['elite-custom-on24-logo'] || ''}
          />
        </div>
        <PageLayoutShell>
          {children}
        </PageLayoutShell>
      </>
    );
  }
  return (<LoadingOverlay />);
};

AuthorizedPageWrapper.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};
